import React from "react";

import { dollarDisplay } from "EmployerApplication/CostEstimationUtils";
import { FieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { validateRequired, validateZero, validateHundred, validateNumber, composeValidators } from "Utils/Validations";

import { Body1 } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Dropdown, MenuDropdown, Option } from "Cobalt/Dropdown";
import { Table, TH, TD, TR, TDFoot, ExpandingRow } from "Cobalt/Table";
import { employeeProfileOverallError } from "Common/EnterAges/Selectors";
import { SIFinalFormField, SIFormInput } from "Common/forms";
import Icon from "Common/Icon";
import { SizeSwitch } from "Common/Responsive";

import makeBem from "Utils/makeBem";

const bem = makeBem("QuoteEmployeePriceBreakdown");

const validateAge = (age) => {
  if (age && age < 18) {
    return "Must be Older than 18";
  }

  if (age && age > 99) {
    return "Must be Younger than 100";
  }
  return undefined;
};

const validatorsNoAge = composeValidators(validateRequired, validateZero, validateHundred, validateNumber);

const validatorsWithAge = composeValidators(validateRequired, validateZero, validateHundred, validateNumber, validateAge);

const AgeField = ({ name, type }) => (
  <SIFinalFormField
    name={`${name}.age`}
    component={SIFormInput}
    validate={type == "Child" ? validatorsNoAge : validatorsWithAge}
    componentProps={{
      type: "number",
      min: 0,
      max: 99,
    }}
    mb={0}
    pl={0}
    className={bem("ageInput")}
    hideErrorText
  />
);

const AddFamilyDropdown = ({ index, hasSpouse, employee, update }) => (
  <Dropdown
    pullRight
    toggle={
      <Link to="#">
        <SizeSwitch xs="+ Family" sm="+ Add Family" />
      </Link>
    }
  >
    {!hasSpouse && (
      <Option
        onSelect={() => {
          const { dependents } = employee;
          dependents.splice(0, 0, { type: "Spouse", age: null });
          update(`employees`, index, { dependents, ...employee });
        }}
      >
        Add Spouse
      </Option>
    )}

    <Option
      onSelect={() => {
        const { dependents } = employee;
        dependents.push({ type: "Child", age: null });
        update(`employees`, index, { dependents, ...employee });
      }}
    >
      Add Child
    </Option>
  </Dropdown>
);

const MobileRow = ({ row, name, remove, update, index, dependentTotals }) => {
  const dependents = row.dependents || [];
  return (
    <ExpandingRow
      className={bem()}
      colspan="4"
      mainContent={
        <>
          <TD className={bem("firstCol")}>{row.name}</TD>
          <TD className={bem("ageCol")}>{AgeField({ name, type: "self" })}</TD>
          <TD className="text-right">
            {AddFamilyDropdown({
              name,
              index,
              employee: row,
              hasSpouse: dependents?.type == "Spouse",
              update,
            })}
          </TD>
        </>
      }
      expandingContent={
        <>
          <TR>
            <TD className={bem("firstCol")}>Employee Share</TD>
            <TD className={bem("ageCol")} />
            <TD className="text-right">{dollarDisplay(row.employeeCost || null)}</TD>
            <TD />
          </TR>
          <TR>
            <TD className={bem("firstCol")}>Company Share</TD>
            <TD className={bem("ageCol")} />
            <TD className="text-right">{dollarDisplay(row.companyCost || null)}</TD>
            <TD />
          </TR>
          <TR>
            <TD className={bem("firstCol")}>Total Cost</TD>
            <TD className={bem("ageCol")} />
            <TD className="text-right">{dollarDisplay(row.premium || null)}</TD>
            <TD />
          </TR>
          <TR>
            <TD colspan={4}>
              <div href="#" className={bem("error")} onClick={() => remove("employees", index)}>
                <Icon icon="trash" pl={2} pr={3} /> Remove Employee
              </div>
            </TD>
          </TR>
          {dependentTotals && dependents.length > 0 && (
            <>
              <TR className={bem("mobileDependentRow")} subRow>
                <TD className={bem("firstCol")} colspan={4}>
                  Dependent Costs
                </TD>
              </TR>
              <TR subRow>
                <TD className={bem("firstCol")}>Employee Share</TD>
                <TD className={bem("ageCol")} />
                <TD className="text-right">{dollarDisplay(dependentTotals.employeeCost || null)}</TD>
                <TD />
              </TR>
              <TR subRow>
                <TD className={bem("firstCol")}>Company Share</TD>
                <TD className={bem("ageCol")} />
                <TD className="text-right">{dollarDisplay(dependentTotals.companyCost || null)}</TD>
                <TD />
              </TR>
              <TR subRow>
                <TD className={bem("firstCol")}>Total Cost</TD>
                <TD className={bem("ageCol")} />
                <TD className="text-right">{dollarDisplay(dependentTotals.premium || null)}</TD>
                <TD />
              </TR>
            </>
          )}

          <FieldArray name={`${name}.dependents`}>
            {({ fields }) =>
              fields.map((dependentName, index) =>
                dependents[index] ? (
                  <>
                    <TR className={bem("mobileDependentRow")} subRow>
                      <TD className={bem("firstCol")}>{dependents[index].type}</TD>
                      <TD className={bem("ageCol")}>
                        {AgeField({
                          name: dependentName,
                          type: dependents[index].type,
                        })}
                      </TD>
                      <TD />
                      <TD>
                        <Icon className={bem("removeDependent")} icon="minus-circle" onClick={() => remove(`${name}.dependents`, index)} />
                      </TD>
                    </TR>

                    {!dependentTotals && (
                      <>
                        <TR subRow>
                          <TD className={bem("firstCol")}>Employee Share</TD>
                          <TD className={bem("ageCol")} />
                          <TD className="text-right">{dollarDisplay(dependents[index].employeeCost || null)}</TD>
                          <TD />
                        </TR>
                        <TR subRow>
                          <TD className={bem("firstCol")}>Company Share</TD>
                          <TD className={bem("ageCol")} />
                          <TD className="text-right">{dollarDisplay(dependents[index].companyCost || null)}</TD>
                          <TD />
                        </TR>
                        <TR subRow>
                          <TD className={bem("firstCol")}>Total Cost</TD>
                          <TD className={bem("ageCol")} />
                          <TD className="text-right">{dollarDisplay(dependents[index].premium || null)}</TD>
                          <TD />
                        </TR>
                      </>
                    )}
                  </>
                ) : (
                  <></>
                ),
              )
            }
          </FieldArray>
        </>
      }
    />
  );
};

const DesktopRow = ({ row, name, update, remove, index, dependentTotals }) => {
  if (!row) return <></>;
  const dependents = row.dependents || [];
  return (
    <>
      <TR>
        <TD className="font-normal">{row.name}</TD>
        <TD>{AgeField({ name, type: "self" })}</TD>
        <TD className="text-right">{dollarDisplay(row.employeeCost || null)}</TD>
        <TD className="text-right">{dollarDisplay(row.companyCost || null)}</TD>
        <TD className="text-right">{dollarDisplay(row.premium || null)}</TD>
        <TD className="text-right">
          {AddFamilyDropdown({
            name,
            index,
            employee: row,
            hasSpouse: dependents[0]?.type == "Spouse",
            update,
          })}
        </TD>
        <TD>
          <MenuDropdown pullRight>
            <Option severity="danger" onSelect={() => remove("employees", index)}>
              <Icon icon="trash" pr={3} /> Remove Employee
            </Option>
          </MenuDropdown>
        </TD>
      </TR>
      {dependentTotals && dependents.length > 0 && (
        <TR subRow>
          <TD colspan={2}>Dependent Costs</TD>
          <TD className="text-right">{dollarDisplay(dependentTotals.employeeCost || null)}</TD>
          <TD className="text-right">{dollarDisplay(dependentTotals.companyCost || null)}</TD>
          <TD className="text-right">{dollarDisplay(dependentTotals.premium || null)}</TD>
          <TD colspan={2} />
        </TR>
      )}

      <FieldArray name={`${name}.dependents`}>
        {({ fields }) =>
          fields.map((dependentName, dependentIndex) =>
            dependents[dependentIndex] ? (
              <TR subRow>
                <TD>{dependents[dependentIndex].type}</TD>
                <TD>
                  {AgeField({
                    name: dependentName,
                    type: dependents[dependentIndex].type,
                  })}
                </TD>
                {!dependentTotals ? (
                  <>
                    <TD className="text-right">{dollarDisplay(dependents[dependentIndex].employeeCost || null)}</TD>
                    <TD className="text-right">{dollarDisplay(dependents[dependentIndex].companyCost || null)}</TD>
                    <TD className="text-right">{dollarDisplay(dependents[dependentIndex].premium || null)}</TD>
                  </>
                ) : (
                  <TD colspan={3} />
                )}

                <TD />
                <TD>
                  <Icon className={bem("removeDependent")} icon="minus-circle" onClick={() => remove(`${name}.dependents`, index)} />
                </TD>
              </TR>
            ) : (
              <></>
            ),
          )
        }
      </FieldArray>
    </>
  );
};

export const QuoteEmployeePriceBreakdownTable = ({ rows, arrayMutators: { push, remove, update }, totals }) => {
  const addEmployee = () =>
    push("employees", {
      name: `Employee ${rows.length + 1}`,
      age: null,
      dependents: [],
    });

  const employeeProfileError = useSelector(employeeProfileOverallError);

  return (
    <>
      <SizeSwitch
        xs={
          <Table>
            <thead>
              <TR>
                <TH>Employee</TH>
                <TH>Age</TH>
                <TH />
                <TH />
              </TR>
            </thead>
            <tbody className={bem()}>
              <FieldArray name="employees">
                {({ fields }) =>
                  fields.map((name, index) => (
                    <MobileRow
                      row={fields.value[index]}
                      dependentTotals={rows[index]?.dependentTotals}
                      z
                      name={name}
                      index={index}
                      update={update}
                      remove={remove}
                    />
                  ))
                }
              </FieldArray>
            </tbody>
            <tfoot>
              <TR>
                <TDFoot colspan={4}>
                  <Button color="gray" size="small" onClick={addEmployee}>
                    Add Employees
                  </Button>
                </TDFoot>
              </TR>
            </tfoot>
          </Table>
        }
        sm={
          <Table>
            <thead>
              <TR>
                <TH>Employee</TH>
                <TH>Age</TH>
                <TH className="text-right">Employee Share</TH>
                <TH className="text-right">Company Share</TH>
                <TH className="text-right">Total Cost</TH>
                <TH />
                <TH />
              </TR>
            </thead>
            <tbody className={bem()}>
              <FieldArray name="employees">
                {({ fields }) =>
                  fields.map((name, index) => (
                    <DesktopRow
                      row={fields.value[index]}
                      dependentTotals={rows[index]?.dependentTotals}
                      name={name}
                      index={index}
                      update={update}
                      remove={remove}
                    />
                  ))
                }
              </FieldArray>
            </tbody>
            <tfoot>
              <TR>
                <TDFoot>
                  <Button color="gray" size="small" onClick={addEmployee}>
                    Add Employees
                  </Button>
                </TDFoot>
                <TDFoot />
                <TDFoot className="text-right">{dollarDisplay(totals.totalEmployeeCost.toFixed(2))}</TDFoot>
                <TDFoot className="text-right">{dollarDisplay(totals.totalCompanyCost.toFixed(2))}</TDFoot>
                <TDFoot className="text-right">{dollarDisplay(totals.totalCost.toFixed(2))}</TDFoot>
                <TDFoot />
                <TDFoot />
              </TR>
            </tfoot>
          </Table>
        }
      />

      {employeeProfileError?.errorMessage && (
        <div>
          <Body1 ml={3} className={bem("error")} componentClass="span" weight="bold" error>
            {employeeProfileError?.errorMessage}
          </Body1>
        </div>
      )}
    </>
  );
};

export default QuoteEmployeePriceBreakdownTable;
